
import { supabase } from '../lib/supabase';

export type GoalType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface StudyGoal {
  id: string;
  user_id: string;
  date: string;
  goal_text: string;
  is_completed: boolean;
  checked_at: string | null;
  created_at: string;
  updated_at: string;
  goal_type: GoalType;
  difficulty: number;
}

async function getAuthUser() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.user) return null;
    return session.user;
  } catch {
    return null;
  }
}

/** Fetch goals for a specific date */
export async function fetchGoalsByDate(date: string): Promise<StudyGoal[]> {
  const user = await getAuthUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('study_goals')
    .select('*')
    .eq('user_id', user.id)
    .eq('date', date)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Failed to fetch goals:', error);
    return [];
  }
  return (data as StudyGoal[]) ?? [];
}

/** Fetch goals for a date range */
export async function fetchGoalsByRange(startDate: string, endDate: string): Promise<StudyGoal[]> {
  const user = await getAuthUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('study_goals')
    .select('*')
    .eq('user_id', user.id)
    .eq('goal_type', 'daily')
    .gte('date', startDate)
    .lte('date', endDate)
    .order('date', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Failed to fetch goals range:', error);
    return [];
  }
  return (data as StudyGoal[]) ?? [];
}

/** Add a new goal */
export async function addGoal(date: string, goalText: string, goalType: GoalType = 'daily', difficulty: number = 3): Promise<StudyGoal | null> {
  const user = await getAuthUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('study_goals')
    .insert({
      user_id: user.id,
      date,
      goal_text: goalText,
      goal_type: goalType,
      difficulty,
    })
    .select()
    .maybeSingle();

  if (error) {
    console.error('Failed to add goal:', error);
    return null;
  }
  return data as StudyGoal;
}

/** Toggle goal completion */
export async function toggleGoalCompletion(goalId: string, isCompleted: boolean): Promise<boolean> {
  const { error } = await supabase
    .from('study_goals')
    .update({
      is_completed: isCompleted,
      checked_at: isCompleted ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', goalId);

  if (error) {
    console.error('Failed to toggle goal:', error);
    return false;
  }
  return true;
}

/** Delete a goal */
export async function deleteGoal(goalId: string): Promise<boolean> {
  const { error } = await supabase
    .from('study_goals')
    .delete()
    .eq('id', goalId);

  if (error) {
    console.error('Failed to delete goal:', error);
    return false;
  }
  return true;
}

/** Update goal text and/or difficulty */
export async function updateGoal(goalId: string, goalText: string, difficulty?: number): Promise<boolean> {
  const updateData: Record<string, unknown> = {
    goal_text: goalText,
    updated_at: new Date().toISOString(),
  };
  if (difficulty !== undefined) {
    updateData.difficulty = difficulty;
  }
  const { error } = await supabase
    .from('study_goals')
    .update(updateData)
    .eq('id', goalId);

  if (error) {
    console.error('Failed to update goal:', error);
    return false;
  }
  return true;
}

/** Fetch goals by type with optional date range filter */
export async function fetchGoalsByType(goalType: GoalType, startDate?: string, endDate?: string): Promise<StudyGoal[]> {
  const user = await getAuthUser();
  if (!user) return [];

  let query = supabase
    .from('study_goals')
    .select('*')
    .eq('user_id', user.id)
    .eq('goal_type', goalType);

  if (startDate) {
    query = query.gte('date', startDate);
  }
  if (endDate) {
    query = query.lte('date', endDate);
  }

  query = query.order('created_at', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error('Failed to fetch goals by type:', error);
    return [];
  }
  return (data as StudyGoal[]) ?? [];
}
